/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { gnoment } from 'shared-ui/gnoment';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { FlexRow, FlexCol } from 'sarsaparilla';
import { LINE_ITEMS } from '../../props/permit';
import {
    formatCurrency,
    passTypesToStringMap,
    getFeeByMemberPass,
    convertCartValueToCents,
} from '../../utils/utilsNew';

const GUIDE_FEE_TYPE = 'Guides';

export default class FeeBreakdownByDateRange extends React.Component {
    static propTypes = {
        fees: PropTypes.array.isRequired,
        isHighUseFeeApplicable: PropTypes.bool.isRequired,
        groupMembers: PropTypes.arrayOf(
            PropTypes.shape({
                fName: PropTypes.string,
                mName: PropTypes.string,
                lName: PropTypes.string,
                remark: PropTypes.string,
                passholderType: PropTypes.number,
                passholderId: PropTypes.string,
                startDate: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
                endDate: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
            })
        ),
        guides: PropTypes.array,
        lineItems: LINE_ITEMS,
        entryDate: PropTypes.any,
        exitDate: PropTypes.any,
        permit: PropTypes.object,
    };

    static defaultProps = {
        groupMembers: [],
        guides: [],
        fees: [],
    };

    constructor(props) {
        super(props);
        this.updateFeeList = this.updateFeeList.bind(this);
        this.buildRangeAndFeeObject = this.buildRangeAndFeeObject.bind(this);
        this.renderRangeRows = this.renderRangeRows.bind(this);
    }

    getUseFee(passType) {
        const fee = getFeeByMemberPass(
            passType,
            this.props.fees,
            this.props.isHighUseFeeApplicable
        );
        if (fee) {
            return fee.amount;
        }

        return 0;
    }

    // Building same OBJECT FORMAT as buildRangeAndFeeObject from line items
    buildRangeAndFeeObjectFromLineItems() {
        const { lineItems } = this.props;

        if (isEmpty(lineItems)) return [];

        const rangeAndFeeObjList = [];
        lineItems.forEach((lineItem) => {
            // If Dates & DisplayAs are set, assume it can be built from line items
            if (!isEmpty(lineItem.Dates) && lineItem.DisplayAs) {
                const startDate = gnoment.unix(lineItem.Dates.Start).utc();
                const endDate = gnoment.unix(lineItem.Dates.End).utc();

                let rangeAndFeeUpdated = false;
                for (let i = 0; i < rangeAndFeeObjList.length; i += 1) {
                    const r = rangeAndFeeObjList[i];
                    if (r.start.isSame(startDate) && r.end.isSame(endDate)) {
                        r[lineItem.DisplayAs] = {
                            num: lineItem.Quantity,
                            fee: convertCartValueToCents(lineItem.UnitPrice),
                        };
                        rangeAndFeeUpdated = true;
                        break;
                    }
                }

                // If couldn't find the dates for the line item, add a new obj to rangeAndFeeObjList
                if (!rangeAndFeeUpdated) {
                    const rangeAndFeeObj = {
                        start: startDate,
                        end: endDate,
                        [lineItem.DisplayAs]: {
                            num: lineItem.Quantity,
                            fee: convertCartValueToCents(lineItem.UnitPrice),
                        },
                    };
                    rangeAndFeeObjList.push(rangeAndFeeObj);
                }
            }
        });

        return rangeAndFeeObjList;
    }

    buildRangeAndFeeObject() {
        const { groupMembers, guides, entryDate, exitDate } = this.props;
        const objs = [];
        /* OBJECT FORMAT
      {
        "start": <gnoment>,
        "end": <gnoment>,
        "Adult/Youth": {"nums": 1},
        "Access Pass": {"nums": 1}
        "Senior Pass": {"nums": 1},
        "Local Pass": {"nums": 1}
      }
    */

        // First check if it can be built from line items (depends on what BE sends)
        const rangeObjFromLineItems = this.buildRangeAndFeeObjectFromLineItems();
        if (rangeObjFromLineItems.length > 0) return rangeObjFromLineItems;

        for (let i = 0; i < groupMembers.length; i += 1) {
            if (
                gnoment.isGnoment(groupMembers[i].startDate) &&
                gnoment.isGnoment(groupMembers[i].endDate)
            ) {
                const rangeObj = this.constructRange(groupMembers[i]);

                const numMatchingRanges = objs.filter((r) => {
                    return r.start.isSame(rangeObj.start) && r.end.isSame(rangeObj.end);
                }).length;

                if (numMatchingRanges < 1) {
                    objs.push(rangeObj);
                }

                const matchedRangeIndex = objs.findIndex((r) => {
                    return r.start.isSame(rangeObj.start) && r.end.isSame(rangeObj.end);
                });

                if (matchedRangeIndex === -1) {
                    // no existing date ranges
                    objs.push(rangeObj);
                } else {
                    objs[matchedRangeIndex] = this.updateFeeList(
                        objs[matchedRangeIndex],
                        groupMembers[i]
                    );
                }
            }
        }

        // account for guides
        const start = entryDate;
        const end = exitDate || entryDate;
        if (guides.length) {
            let guidesWereAdded = false;
            for (let i = 0; i < objs.length; i += 1) {
                const r = objs[i];
                if (r.start.isSame(start) && r.end.isSame(end)) {
                    guidesWereAdded = true;
                    objs[i] = { ...r, [GUIDE_FEE_TYPE]: { num: guides.length } };
                    break;
                }
            }
            if (!guidesWereAdded) {
                objs.push({
                    [GUIDE_FEE_TYPE]: { num: guides.length },
                    start,
                    end,
                });
            }
        }

        return objs;
    }

    updateFeeList(rangeObj, member) {
        const newObj = { ...rangeObj };
        let passType = passTypesToStringMap.get(0);
        if (member.passholderType) {
            passType = passTypesToStringMap.get(member.passholderType);
        }
        if (rangeObj[passType]) {
            newObj[passType].num += 1;
        } else {
            newObj[passType] = { num: 1 };
        }
        return newObj;
    }

    constructRange(member) {
        return {
            start: member.startDate.utc(true).startOf('day'),
            end: member.endDate.utc(true).startOf('day'),
        };
    }

    // each distinct date range
    // ex: "Mar 23 - Mar 26 (4 days)"
    renderRangeRows(ranges) {
        /* ranges = [ {"start": <gnoment>, "end": <gnoment>, "Adult/Youth Pass": {"nums": 1}, "Senior Pass": {"nums": 1}} ] */
        return ranges.map((r) => {
            const numDays = r.end.diff(r.start, 'days') + 1;
            const datesStr = `${r.start.format('MM-DD')}--${r.end.format('MM-DD')}`;
            const id = `range-${datesStr}`;
            return (
                <div id={id} key={id}>
                    <div className="rec-sr-only" id={`${id}-text`}>
                        {r.start.format('MMM DD')} - {r.end.format('MMM DD')} ({numDays}{' '}
                        {numDays === 1 ? 'day' : 'days'})
                    </div>
                    {this.renderPassRows(r, numDays, datesStr)}
                </div>
            );
        });
    }

    // each pass type (subset of the ranges)
    // ex: "Adult/Youth Pass    1 x $4.00    $12.00"
    renderPassRows(rangeObj, numDays, datesStr) {
        const rangeObjClone = cloneDeep(rangeObj);
        delete rangeObjClone.start;
        delete rangeObjClone.end;

        const html = Object.keys(rangeObjClone).map((key) => {
            const passType = rangeObjClone[key];
            // const passTypeString = passTypesToString.get(i).replace(/[\s/]+/g, '') || i; // remove spaces and slashes from string
            const passTypeString = key.replace(/[\s/]+/g, '');
            const id = `pass-${datesStr}-${passTypeString}`; // pass-07/04-07/08-Adult/Youth
            const useFee = passType.fee || this.getUseFee(passTypesToStringMap.get(key));
            const total = numDays * parseInt(useFee, 10) * parseInt(passType.num, 10);
            return (
                <FlexRow key={id} id={id}>
                    <FlexCol xs={5}>
                        <span id={`${id}-name`}>{key}</span>
                    </FlexCol>
                    <FlexCol xs={4}>
                        <span id={`${id}-eq`}>
                            {passType.num} x {formatCurrency(useFee)}
                        </span>
                    </FlexCol>
                    <FlexCol className="rec-price-value" xs={3}>
                        <span id={`${id}-total`}>{formatCurrency(total)}</span>
                    </FlexCol>
                </FlexRow>
            );
        });
        return html;
    }

    render() {
        const ranges = this.buildRangeAndFeeObject();
        if (ranges.length > 0) {
            return (
                <div data-test-id="fee-breakdown-by-date-range">
                    {this.renderRangeRows(ranges)}
                </div>
            );
        }
        return null;
    }
}
